import { useState } from 'react'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { Card, CardContent, CardHeader, Stack } from '@mui/material'
import { RootState } from '../redux'
import useLifts from '../hooks/useLifts'
import { usePolicy } from '../contexts/PolicyContext'
import LoadingState from './LoadingState'
import EmptyState from './EmptyState'
import { Lift, Project } from '../types'
import { DataGrid, GridColDef, GridPaginationModel } from '@mui/x-data-grid'
import LiftDeleteButton from './LiftDeleteButton'
import LiftEditButton from './LiftEditButton'
import LiftCreateButton from './LiftCreateButton'
import { ProjectStateType } from '../enums/ProjectStateType'

export interface LiftTableProps {
  project?: Project
}

export default function LiftTable({ project }: LiftTableProps) {
  /**
   * The translate function.
   */
  const [t] = useTranslation('common')

  /**
   * The policies.
   */
  const { canEditProject } = usePolicy()

  /**
   * The lifts.
   */
  const lifts: Lift[] = useLifts()

  /**
   * Indicates if lifts are being loaded.
   */
  const isLoading: boolean = useSelector((state: RootState) => state.lifts.loading)

  /**
   * Indicates if there are no lifts.
   */
  const isEmpty: boolean = lifts.length === 0

  /**
   * The grid columns.
   */
  const columns: GridColDef<Lift>[] = [
    {
      field: 'name',
      headerName: t('name'),
      width: 200,
      editable: false
    },
    {
      field: 'sector.name',
      headerName: t('Project.sector'),
      minWidth: 200,
      flex: 1,
      editable: false,
      valueGetter: (_, row) => row.sector?.name ?? '-'
    }
  ]

  if (project && project.state !== ProjectStateType.ARCHIVED && canEditProject(project)) {
    columns.push({
      field: t('actions'),
      type: 'actions',
      headerName: t('actions'),
      headerAlign: 'center',
      align: 'center',
      sortable: false,
      filterable: false,
      resizable: false,
      disableColumnMenu: true,
      renderCell: ({ row }) => (
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="center"
          flexGrow={0}
          spacing={1}
          height="100%"
        >
          <LiftEditButton project={project} lift={row} />
          <LiftDeleteButton project={project} lift={row} />
        </Stack>
      )
    })
  }

  /**
   * The pagination model.
   */
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    pageSize: 25,
    page: 0
  })

  return (
    <Card>
      <CardHeader title={t('lifts')} action={<LiftCreateButton project={project} />} />
      <CardContent>
        {isLoading ? (
          <LoadingState />
        ) : isEmpty ? (
          <EmptyState title={t('noLifts')} />
        ) : (
          <DataGrid
            rows={lifts}
            columns={columns}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            disableRowSelectionOnClick
          />
        )}
      </CardContent>
    </Card>
  )
}
